/**
 * Places Service - Google Places API 封裝（新版 Places API）
 * 使用 google.maps.places.AutocompleteSuggestion + google.maps.places.Place
 * 需在 APIProvider context 內（places library 已載入）使用
 */

export interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  placePrediction: google.maps.places.PlacePrediction;
}

/**
 * 取得 Places Autocomplete 建議清單
 */
export async function getPlacePredictions(
  input: string,
  sessionToken?: google.maps.places.AutocompleteSessionToken
): Promise<PlaceSuggestion[]> {
  if (input.length < 2) return [];

  try {
    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken,
        language: navigator.language,
      });

    return suggestions
      .filter((s) => s.placePrediction != null)
      .map((s) => ({
        placeId: s.placePrediction!.placeId,
        mainText: s.placePrediction!.mainText?.text ?? '',
        secondaryText: s.placePrediction!.secondaryText?.text ?? '',
        placePrediction: s.placePrediction!,
      }));
  } catch {
    return [];
  }
}

export interface PlaceDetailResult {
  name: string;
  lat: number;
  lng: number;
  address: string;
}

/**
 * 取得地點完整資訊（從 PlacePrediction 選取後呼叫）
 */
export async function getPlaceDetails(
  placePrediction: google.maps.places.PlacePrediction
): Promise<PlaceDetailResult | null> {
  try {
    const place = placePrediction.toPlace();
    await place.fetchFields({ fields: ['displayName', 'location', 'formattedAddress'] });

    if (!place.location) return null;

    return {
      name: place.displayName ?? '',
      lat: place.location.lat(),
      lng: place.location.lng(),
      address: place.formattedAddress ?? '',
    };
  } catch {
    return null;
  }
}
